(function () {
  'use strict';

  if (window.RFQ_FETCH_GUARD_DISABLED) { return; }
  if (window.__rfqFetchGuardInstalled) { return; }
  if (typeof window.fetch !== 'function' || typeof window.Response !== 'function') { return; }
  window.__rfqFetchGuardInstalled = true;

  var ENDPOINT      = '/apps/quote/front/api/search';
  var CACHE_TTL     = 5 * 60 * 1000;  // ms a cached answer stays good
  var MIN_GAP       = 400;            // ms between two real requests
  var MAX_ATTEMPTS  = 4;              // total tries per request
  var BASE_BACKOFF  = 1000;
  var MAX_BACKOFF   = 8000;
  var MAX_ENTRIES   = 80;             // cap on what we persist
  var STORAGE_KEY   = 'rfq_fetch_guard_v1';

  var nativeFetch   = window.fetch.bind(window);
  var memory        = readStore();
  var inflight      = {};
  var queue         = [];
  var pumping       = false;
  var lastSent      = 0;
  var cooldownUntil = 0;

  function log() {
    if (!window.RFQ_FETCH_GUARD_DEBUG) { return; }
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[rfq-guard]');
    console.log.apply(console, args);
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* ---------------------------------------------------------------- cache */

  function readStore() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) { return {}; }
      var parsed = JSON.parse(raw);
      var now = Date.now();
      var kept = {};
      Object.keys(parsed).forEach(function (key) {
        if (parsed[key] && now - parsed[key].time < CACHE_TTL) { kept[key] = parsed[key]; }
      });
      return kept;
    } catch (e) {
      return {};
    }
  }

  function writeStore() {
    try {
      var keys = Object.keys(memory).sort(function (a, b) {
        return memory[b].time - memory[a].time;
      });
      if (keys.length > MAX_ENTRIES) {
        keys.slice(MAX_ENTRIES).forEach(function (key) { delete memory[key]; });
      }
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
    } catch (e) {
      /* private mode / quota -- the in-memory cache still works for this page */
    }
  }

  // Same handles in a different order are the same answer, so sort them.
  function cacheKey(url) {
    var parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (e) {
      return url;
    }
    var handles = (parsed.searchParams.get('handles') || '')
      .split('|')
      .filter(Boolean)
      .sort()
      .join('|');
    return [
      parsed.pathname,
      parsed.searchParams.get('action') || '',
      parsed.searchParams.get('view') || '',
      handles
    ].join('::');
  }

  function toResponse(record) {
    return new window.Response(record.body, {
      status: record.status,
      statusText: record.statusText || '',
      headers: { 'Content-Type': record.type }
    });
  }

  /* ---------------------------------------------------------------- queue */

  function enqueue(task) {
    return new Promise(function (resolve, reject) {
      queue.push({ task: task, resolve: resolve, reject: reject });
      pump();
    });
  }

  function pump() {
    if (pumping || !queue.length) { return; }
    pumping = true;

    var now = Date.now();
    var delay = Math.max(cooldownUntil - now, lastSent + MIN_GAP - now, 0);

    setTimeout(function () {
      var job = queue.shift();
      lastSent = Date.now();
      var done = function () { pumping = false; pump(); };
      job.task().then(job.resolve, job.reject).then(done, done);
    }, delay);
  }

  /* -------------------------------------------------------------- request */

  function send(url, init, attempt) {
    return nativeFetch(url, init).then(function (res) {
      if (res.status !== 429 || attempt >= MAX_ATTEMPTS) {
        if (res.status === 429) {
          console.warn('[rfq-guard] still rate limited after ' + attempt + ' attempts:', url);
        }
        return res;
      }
      var retryAfter = parseFloat(res.headers.get('Retry-After'));
      var backoff = (isFinite(retryAfter) && retryAfter > 0)
        ? retryAfter * 1000
        : Math.min(BASE_BACKOFF * Math.pow(2, attempt - 1), MAX_BACKOFF);

      cooldownUntil = Date.now() + backoff;
      log('429 on attempt ' + attempt + ', retrying in ' + backoff + 'ms');
      return wait(backoff).then(function () { return send(url, init, attempt + 1); });
    });
  }

  function guarded(url, init) {
    var key = cacheKey(url);
    var hit = memory[key];

    if (hit && Date.now() - hit.time < CACHE_TTL) {
      log('cache hit', key);
      return Promise.resolve(toResponse(hit));
    }

    if (inflight[key]) {
      log('joined in-flight request', key);
      return inflight[key].then(toResponse);
    }

    var pending = enqueue(function () { return send(url, init, 1); })
      .then(function (res) {
        return res.text().then(function (body) {
          var record = {
            time: Date.now(),
            status: res.status,
            statusText: res.statusText,
            type: res.headers.get('Content-Type') || 'application/json',
            body: body
          };
          if (res.ok && res.status >= 200) {
            memory[key] = record;
            writeStore();
            log('cached', key);
          }
          return record;
        });
      });

    inflight[key] = pending;
    var clear = function () { delete inflight[key]; };
    pending.then(clear, clear);

    return pending.then(toResponse);
  }

  /* ---------------------------------------------------------------- patch */

  window.fetch = function (input, init) {
    var url = (typeof input === 'string')
      ? input
      : (input && typeof input.url === 'string' ? input.url : '');

    var method = (init && init.method) || (input && input.method) || 'GET';
    var isTarget = typeof input === 'string' &&
                   url.indexOf(ENDPOINT) !== -1 &&
                   String(method).toUpperCase() === 'GET';

    if (!isTarget) { return nativeFetch(input, init); }

    try {
      return guarded(url, init);
    } catch (e) {
      console.warn('[rfq-guard] falling back to native fetch:', e);
      return nativeFetch(input, init);
    }
  };

  log('installed');
})();
